import Feature from 'ol/Feature';
import { readStringDisplayNo, readStringSeq } from '../../utils/panelMapFeatureProps.js';
import { logStringCopySave } from '../../utils/stringCopySaveDebug.js';
import {
  panelPointerMovePerfBeginHandler,
  panelPointerMovePerfEndHandler
} from '../../utils/panelPointerMovePerfLog.js';

/**
 * Ctrl+드래그 복사 플러그인 — geometry.translate() 방식
 * Shift 다중 선택 String → 함께 드래그·놓기 시 각 String을 단일 복사와 동일하게 저장
 */
export const panelCtrlDragCopyPlugin = {
  id: 'panel-ctrl-drag-copy',

  attach(host, ctx = {}) {
    const map = host.getMap();
    const viewport = host.getViewport();
    if (!map || !viewport) return;

    /**
     * @type {{
     *   startCoord: number[],
     *   items: Array<object>,
     *   pointerId: number
     * } | null}
     */
    let ctrlDragSession = null;

    const removeSessionClones = session => {
      if (!session?.items?.length) {
        return;
      }
      const source = ctx.getVectorSource?.();
      session.items.forEach(({ stringClone, moduleClones }) => {
        if (stringClone) {
          source?.removeFeature(stringClone);
        }
        moduleClones?.forEach(mc => source?.removeFeature(mc));
      });
    };

    const clearSession = ({ removeClones = true } = {}) => {
      if (!ctrlDragSession) {
        return;
      }
      if (removeClones) {
        removeSessionClones(ctrlDragSession);
      }
      ctrlDragSession = null;
    };

    let ctrlModifierSuspended = false;

    const suspendForCtrlCopy = () => {
      if (ctrlModifierSuspended) {
        return;
      }
      ctx.suspendMainMapInteractionsForCtrlDrag?.();
      ctrlModifierSuspended = true;
    };

    const restoreAfterCtrlCopy = () => {
      if (!ctrlModifierSuspended) {
        return;
      }
      ctx.restoreMainMapInteractionsAfterCtrlDrag?.();
      ctrlModifierSuspended = false;
    };

    const resolveHitFeature = pixel => {
      const hit = ctx.getMapClickTargetFeatureAtPixel?.(pixel);
      if (!hit || hit.get?.('isCtrlDragClone')) {
        return null;
      }
      return ctx.resolveCtrlDragSourceFeature?.(hit) ?? hit;
    };

    const buildSessionItems = stringFeatures => {
      const source = ctx.getVectorSource?.();
      return stringFeatures.map(original => {
        const stringClone = original.clone();
        stringClone.set('isCtrlDragClone', true);
        stringClone.set('name', `${original.get('name') || ''} (복사)`);
        source?.addFeature(stringClone);

        const moduleClones = ctx.createCtrlDragModuleClones?.(original) ?? [];

        return {
          original,
          stringClone,
          moduleClones,
          initialStringGeometry: original.getGeometry()?.clone?.(),
          initialModuleGeometries: moduleClones.map(mc => mc.getGeometry()?.clone?.())
        };
      });
    };

    const releasePointerCapture = pointerId => {
      if (pointerId == null) {
        return;
      }
      try {
        if (viewport.hasPointerCapture?.(pointerId)) {
          viewport.releasePointerCapture(pointerId);
        }
      } catch {
        // ignore
      }
    };

    const finishSession = ({ removeClones = true, restoreUi = true } = {}) => {
      ctx.clearStringPlacementPreview?.();
      const pointerId = ctrlDragSession?.pointerId;
      clearSession({ removeClones });
      releasePointerCapture(pointerId);
      ctx.setDragPanActive?.(true);
      ctx.setCtrlDragActive?.(false);
      restoreAfterCtrlCopy();
      if (restoreUi) {
        ctx.restoreMapUiAfterInteractionCancel?.();
      }
    };

    /* ---- Ctrl 누르는 순간 Translate 제거 (이동 vs 복사 분리) ---- */
    const onKeyDown = event => {
      if (event.key === 'Escape' && ctrlDragSession) {
        event.preventDefault();
        finishSession();
        return;
      }
      if (event.key === 'Control' || event.key === 'Meta') {
        ctx.setCtrlKeyPressed?.(true);
        ctx.setDragPanActive?.(false);
        suspendForCtrlCopy();
      }
    };
    const onKeyUp = event => {
      if (event.key === 'Control' || event.key === 'Meta') {
        ctx.setCtrlKeyPressed?.(false);
        if (!ctrlDragSession) {
          ctx.setDragPanActive?.(true);
          restoreAfterCtrlCopy();
        }
      }
    };
    const onWindowBlur = () => {
      ctx.setCtrlKeyPressed?.(false);
      if (ctrlDragSession) {
        finishSession();
      } else {
        restoreAfterCtrlCopy();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onWindowBlur);

    const getSessionDropCenter = session => {
      const stringClone = session?.items?.[0]?.stringClone;
      const geometry = stringClone?.getGeometry?.();
      if (!geometry) return null;
      const extent = geometry.getExtent();
      return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    };

    const publishPlacementPreview = session => {
      const dropCenter = getSessionDropCenter(session);
      const sourceFeature = session?.items?.[0]?.original;
      if (!dropCenter || !sourceFeature) return;
      ctx.updateStringPlacementPreview?.({
        dropCoordinate: dropCenter,
        sourceFeature
      });
    };

    const onPointerDown = event => {
      if (!(event.ctrlKey || event.metaKey) || event.button !== 0) {
        return;
      }
      if (ctx.isDrawingActive?.()) {
        return;
      }

      suspendForCtrlCopy();

      const pixel = map.getEventPixel(event);
      const hit = resolveHitFeature(pixel);
      if (!hit) {
        return;
      }

      const stringFeatures = ctx.getSelectedStringFeaturesForCtrlDrag?.(hit) ?? [];
      if (!stringFeatures.length) {
        return;
      }

      ctx.syncStringDrawTargetFromMapFeature?.(stringFeatures[0]);

      event.preventDefault();
      event.stopPropagation();

      ctx.removeSelectionHighlight?.();
      ctx.setDragPanActive?.(false);
      ctx.setCtrlDragActive?.(true);

      try {
        viewport.setPointerCapture(event.pointerId);
      } catch {
        // ignore
      }

      ctrlDragSession = {
        startCoord: map.getCoordinateFromPixel(pixel),
        items: buildSessionItems(stringFeatures),
        pointerId: event.pointerId
      };
      publishPlacementPreview(ctrlDragSession);
    };

    const onPointerMove = event => {
      const perf = panelPointerMovePerfBeginHandler('ctrl-drag-copy');
      if (!ctrlDragSession) {
        panelPointerMovePerfEndHandler(perf, { active: false });
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      const coord = map.getCoordinateFromPixel(map.getEventPixel(event));
      const delta = [
        coord[0] - ctrlDragSession.startCoord[0],
        coord[1] - ctrlDragSession.startCoord[1]
      ];

      ctrlDragSession.items.forEach(
        ({ stringClone, moduleClones, initialStringGeometry, initialModuleGeometries }) => {
          if (initialStringGeometry && stringClone) {
            const nextStringGeometry = initialStringGeometry.clone();
            nextStringGeometry.translate(delta[0], delta[1]);
            stringClone.setGeometry(nextStringGeometry);
          }

          moduleClones.forEach((moduleClone, index) => {
            const initialModuleGeometry = initialModuleGeometries?.[index];
            if (initialModuleGeometry) {
              const nextModuleGeometry = initialModuleGeometry.clone();
              nextModuleGeometry.translate(delta[0], delta[1]);
              moduleClone.setGeometry(nextModuleGeometry);
            }
          });
        }
      );

      publishPlacementPreview(ctrlDragSession);
      panelPointerMovePerfEndHandler(perf, {
        active: true,
        itemCount: ctrlDragSession.items.length
      });
    };

    const onPointerUp = async event => {
      if (!ctrlDragSession?.items?.length) {
        return;
      }

      if (event.button === 2) {
        cancelCtrlDragSession(event);
        return;
      }
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const session = ctrlDragSession;
      ctrlDragSession = null;
      ctx.clearStringPlacementPreview?.();
      releasePointerCapture(session.pointerId);
      ctx.setDragPanActive?.(true);
      ctx.setCtrlDragActive?.(false);
      restoreAfterCtrlCopy();

      const featureType = (session.items[0].original.get('featureType') || '').toLowerCase();
      if (featureType === 'string') {
        if (session.items.length === 1) {
          const { original, stringClone, moduleClones } = session.items[0];
          ctx.getVectorSource?.()?.removeFeature(stringClone);
          moduleClones?.forEach(mc => ctx.getVectorSource?.()?.removeFeature(mc));

          const geom = stringClone.getGeometry()?.clone?.();
          if (!geom) {
            ctx.restoreMapUiAfterInteractionCancel?.();
            return;
          }

          const drawnFeature = new Feature({ geometry: geom });
          const { rowNum, colNum } = ctx.resolveSourceStringRowCol?.(original) ?? {};
          ctx.setDrawnPolygonFromApiRowCol?.(drawnFeature, rowNum, colNum);

          const extent = geom.getExtent();
          const dropCenter = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
          const sourceArraySeq =
            original.get('solarArraySeq') ?? original.get('solarArrayId') ?? null;
          const targetArraySeq =
            ctx.resolveStringPlacementArraySeqForDrop?.({
              dropCoordinate: dropCenter,
              sourceFeature: original,
              sourceArraySeq
            }) ?? sourceArraySeq;

          if (targetArraySeq == null) {
            ctx.warnStringDrawTargetArrayRequired?.();
            ctx.restoreMapUiAfterInteractionCancel?.();
            return;
          }

          ctx.syncStringDrawTargetFromArraySeq?.(targetArraySeq);

          const skipGridSlots = ctx.resolveCtrlDragCopySkipSlots?.(original) ?? null;

          logStringCopySave('[CtrlDragCopy] drop (single String) → before confirmDivision', {
            source: {
              stringSeq: readStringSeq(original),
              stringDisplayId: readStringDisplayNo(original),
              arraySeq: sourceArraySeq,
              name: original.get('name') ?? null
            },
            target: { arraySeq: targetArraySeq },
            rowNum,
            colNum,
            skipGridSlotCount: skipGridSlots?.size ?? 0
          });

          await ctx.forceAutoDraw?.(async () => {
            await ctx.confirmDivision?.({
              solarArraySeq: targetArraySeq,
              _ctrlDragCopySourceFeature: original,
              skipGridSlots
            });
          });

          logStringCopySave('[CtrlDragCopy] drop (single String) → after confirmDivision');
          return;
        }

        await ctx.processMultiStringCtrlDragDrop?.(session.items);
        return;
      }

      const { original, stringClone } = session.items[0];
      ctx.getVectorSource?.()?.removeFeature(stringClone);
      const geom = stringClone.getGeometry()?.clone?.();
      if (!geom) {
        ctx.restoreMapUiAfterInteractionCancel?.();
        return;
      }
      const extent = geom.getExtent();
      const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
      ctx.setCopiedFeature?.(ctx.extractFeatureData?.(original), 'module');
      await ctx.pasteFeatureAtCoordinate?.(center);
    };

    const onPointerCancel = () => {
      finishSession();
    };

    const cancelCtrlDragSession = domEvent => {
      if (!ctrlDragSession) return;
      domEvent?.preventDefault?.();
      domEvent?.stopPropagation?.();
      finishSession();
    };

    const onContextMenuCancel = event => {
      if (ctrlDragSession) {
        cancelCtrlDragSession(event);
      }
    };

    viewport.addEventListener('pointerdown', onPointerDown, { capture: true });
    viewport.addEventListener('pointermove', onPointerMove, { capture: true });
    viewport.addEventListener('pointerup', onPointerUp, { capture: true });
    viewport.addEventListener('pointercancel', onPointerCancel, { capture: true });
    viewport.addEventListener('contextmenu', onContextMenuCancel, { capture: true });

    ctx.registerMapInteractionCancelHandlers?.({
      cancelCtrlDragCopy: () => {
        if (ctrlDragSession) {
          cancelCtrlDragSession();
        }
      }
    });

    return () => {
      ctx.setDragPanActive?.(true);
      ctx.setCtrlKeyPressed?.(false);
      ctx.setCtrlDragActive?.(false);

      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onWindowBlur);

      finishSession();
      viewport.removeEventListener('pointerdown', onPointerDown, { capture: true });
      viewport.removeEventListener('pointermove', onPointerMove, { capture: true });
      viewport.removeEventListener('pointerup', onPointerUp, { capture: true });
      viewport.removeEventListener('pointercancel', onPointerCancel, { capture: true });
      viewport.removeEventListener('contextmenu', onContextMenuCancel, { capture: true });
    };
  }
};
